// Content Moderator (/volunteer/moderate): three queues of things students and
// clubs submitted, each needing a yes or a no before anybody else sees it.
//
//   Events              — api/event-review.js
//   Restaurants         — RestaurantsAdmin's review queue, dish photos and
//                         handles panels: reviews (pending, live, denied — any
//                         of them deletable) and students' photos of menu
//                         dishes (the `itemId` rows of /api/place-photo, the
//                         waiting ones and the live ones)
//   Place photos        — the rest of the /api/place-photo queue: photos of
//                         study spots, libraries and amenities. Dish rows are
//                         filtered OUT here; they are moderated under Restaurants.
//
//   Clubs               — verifying a club ACCOUNT (the older, coarser gate:
//                         nothing an unverified club makes is public). The
//                         operator gets the whole of Events Admin here — links,
//                         addresses, adding and deleting logins — because /admin
//                         no longer mounts it; a volunteer gets verify/unverify
//                         only (api/event-review.js `club` op), since everything
//                         else touches a club's credential.
//
// ⚠️ Place photos are here because /admin's Photos tab is being retired (the
// Admin Revamp), and a public submission path with no moderation screen is a
// queue that silently fills forever. Nothing submitted there is public until
// somebody accepts it, so without this tab nothing would ever be.

import { useCallback, useEffect, useState } from 'react';
import RestaurantsAdmin from '../components/RestaurantsAdmin.js';
import EventsAdmin from '../components/EventsAdmin.js';
import EventModeration from './EventModeration.jsx';
import { API_BASE, api, ago } from './shared.js';

const TABS = [
  { id: 'events', label: 'Events' },
  { id: 'reviews', label: 'Restaurants' },
  { id: 'photos', label: 'Place photos' },
  { id: 'clubs', label: 'Clubs' },
];

export default function Moderator({ token, onExpired, admin = false }) {
  const [tab, setTab] = useState('events');
  const [counts, setCounts] = useState({});
  const setCount = useCallback((id, n) => setCounts((c) => (c[id] === n ? c : { ...c, [id]: n })), []);
  // Stable, or each child's load effect would re-run on every render here.
  const onEvents = useCallback((n) => setCount('events', n), [setCount]);
  const onReviews = useCallback((n) => setCount('reviews', n), [setCount]);
  const onPhotos = useCallback((n) => setCount('photos', n), [setCount]);
  const onClubs = useCallback((n) => setCount('clubs', n), [setCount]);

  return (
    <>
      <h1 className="vt-h1">Content Moderator</h1>
      <p className="vt-sub">New things wait here until you approve them. Nothing in a queue is public yet.</p>
      <div className="vt-tabs" role="group" aria-label="Queue">
        {TABS.map((t) => (
          <button key={t.id} className="vt-tab" aria-pressed={tab === t.id} onClick={() => setTab(t.id)}>
            {t.label}
            {counts[t.id] > 0 && <span className="vt-count vt-count-hot">{counts[t.id]}</span>}
          </button>
        ))}
      </div>
      {/* All three stay mounted so the counts on the tabs are real from the start. */}
      <div hidden={tab !== 'events'}>
        <EventModeration token={token} onExpired={onExpired} onCount={onEvents} />
      </div>
      <div hidden={tab !== 'reviews'}>
        <RestaurantsAdmin token={token} onExpired={onExpired} panels={['queue', 'photos', 'handles']} onCount={onReviews} />
      </div>
      <div hidden={tab !== 'photos'}>
        <PlacePhotoQueue token={token} onExpired={onExpired} onCount={onPhotos} />
      </div>
      <div hidden={tab !== 'clubs'}>
        {admin
          ? (tab === 'clubs' && <EventsAdmin apiBase={API_BASE} token={token} onUnauthorized={onExpired} />)
          : <ClubQueue token={token} onExpired={onExpired} onCount={onClubs} />}
      </div>
    </>
  );
}

/** A stored photo path → a loadable URL (Blob absolute, or dev-machine relative). */
const photoUrl = (u) => (/^(https?:|data:|blob:)/i.test(String(u || ''))
  ? u : encodeURI(`/Map Info/UofMPics/${u}`));

function PlacePhotoQueue({ token, onExpired, onCount }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    const r = await api('/api/place-photo?part=admin', { token, onExpired });
    if (!r.ok) { setErr(r.body.error || 'Could not load the photo queue.'); return; }
    setErr('');
    // Dish photos are moderated under Restaurants (RestaurantsAdmin → Dish photos).
    const places = (r.body.pending || []).filter((p) => !p.itemId);
    setRows(places);
    onCount(places.length);
  }, [token, onExpired, onCount]);
  useEffect(() => { load(); }, [load]);

  const decide = async (row, op) => {
    setBusy(row.id);
    const r = await api('/api/place-photo', { method: 'POST', token, onExpired, body: { op, id: row.id } });
    setBusy('');
    if (!r.ok) { setErr(r.body.error || 'That did not go through.'); return; }
    await load();
  };

  if (!rows) return <div className="vt-empty">{err || 'Loading…'}</div>;
  return (
    <>
      <p className="vt-sub">Photos students took of study spots, libraries and other places. Accept the ones that show the place clearly and show nobody’s face. Menu-dish photos are under <b>Restaurants</b>.</p>
      {err && <div className="vt-err">{err}</div>}
      {!rows.length ? <div className="vt-empty">No photos waiting. 🎉</div> : (
        <div className="vt-photos" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))' }}>
          {rows.map((p) => (
            <div key={p.id} className="vt-card" style={{ overflow: 'hidden' }}>
              <a href={photoUrl(p.url)} target="_blank" rel="noopener noreferrer" className="vt-photo" style={{ display: 'block', borderRadius: 0 }}>
                <img src={photoUrl(p.url)} alt={`Submitted by a student: ${p.itemName || p.placeName}`} loading="lazy" />
              </a>
              <div style={{ padding: 10 }}>
                {/* A dish photo names the dish first: "Fried Eggs" is what the
                    picture has to show, and the counter is where it is sold. */}
                <div className="vt-title" style={{ fontSize: 14 }}>{p.itemId ? `${p.itemName} — ${p.placeName}` : p.placeName}</div>
                <div className="vt-meta">{p.itemId ? 'Menu photo' : [p.building, p.roomNumber].filter(Boolean).join(' ')}{p.ts ? ` · ${ago(p.ts)}` : ''}</div>
                <div className="vt-actions" style={{ justifyContent: 'flex-start', marginTop: 8 }}>
                  <button className="vt-btn vt-good vt-small" disabled={busy === p.id} onClick={() => decide(p, 'accept')}>Accept</button>
                  <button className="vt-btn vt-danger vt-small" disabled={busy === p.id} onClick={() => decide(p, 'reject')}>Reject</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * Verify a club, or take verification back — the volunteer's half of Events
 * Admin. Unverified first, because that is the queue; a club with events
 * waiting leads it, since verifying it is what those events are waiting on.
 */
function ClubQueue({ token, onExpired, onCount }) {
  const [clubs, setClubs] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState('');
  const [showVerified, setShowVerified] = useState(false);

  const load = useCallback(async () => {
    const r = await api('/api/event-review', { token, onExpired });
    if (!r.ok) { setErr(r.body.error || 'Could not load clubs.'); return; }
    setErr('');
    setClubs(r.body.clubs || []);
    onCount((r.body.clubs || []).filter((c) => !c.approved && c.events > 0).length);
  }, [token, onExpired, onCount]);
  useEffect(() => { load(); }, [load]);

  const verify = async (c, approved) => {
    setBusy(c.id); setMsg(''); setErr('');
    const r = await api('/api/event-review', { method: 'POST', token, onExpired, body: { op: 'club', accountId: c.id, approved } });
    setBusy('');
    if (!r.ok) { setErr(r.body.error || 'That did not go through.'); return; }
    setMsg(approved
      ? `${c.displayName} is verified.${r.body.emailed ? ' We emailed them.' : r.body.emailError ? ` (The email did not send: ${r.body.emailError})` : ''}`
      : `${c.displayName} is no longer verified — its events are off the map until it is verified again.`);
    await load();
  };

  if (!clubs) return <div className="vt-empty">{err || 'Loading clubs…'}</div>;
  const waiting = clubs.filter((c) => !c.approved)
    .sort((a, z) => (z.events - a.events) || String(a.displayName).localeCompare(z.displayName));
  const verified = clubs.filter((c) => c.approved).sort((a, z) => String(a.displayName).localeCompare(z.displayName));

  const row = (c) => (
    <div key={c.id} className="vt-row">
      <div className="vt-rowhead">
        {c.logo ? <img className="vt-logo" src={c.logo} alt="" /> : <span className="vt-logoNone" aria-hidden="true">🎪</span>}
        <div className="vt-grow">
          <div className="vt-title">{c.displayName} {c.approved
            ? <span className="vt-pill vt-pill-good">Verified</span>
            : <span className="vt-pill vt-pill-warn">Not verified</span>}</div>
          <div className="vt-meta">
            {c.events} event{c.events === 1 ? '' : 's'}{c.pendingEvents ? ` · ${c.pendingEvents} waiting for review` : ''}
            {' · '}{c.firstSignInAt ? `first signed in ${ago(c.firstSignInAt)}` : 'has never signed in'}
            {!c.ready ? ' · profile incomplete (no logo or contact)' : ''}
          </div>
        </div>
        <div className="vt-actions">
          {c.approved
            ? <button className="vt-btn vt-danger" disabled={busy === c.id} onClick={() => verify(c, false)}>Unverify</button>
            : <button className="vt-btn vt-good" disabled={busy === c.id} onClick={() => verify(c, true)}>Verify club</button>}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <p className="vt-sub">
        A club has to be verified before anything it makes is public. Verify groups you can confirm are real student
        groups; each event still gets its own review. Sign-in links and email changes are handled by UMaps.
      </p>
      {err && <div className="vt-err">{err}</div>}
      {msg && <div className="vt-ok">{msg}</div>}
      <div className="vt-list">
        {waiting.length ? waiting.map(row) : <div className="vt-empty">Every club is verified.</div>}
      </div>
      {verified.length > 0 && (
        <>
          <button className="vt-btn vt-ghost" style={{ marginTop: 14 }} onClick={() => setShowVerified((v) => !v)}>
            {showVerified ? '▾' : '▸'} {verified.length} verified club{verified.length === 1 ? '' : 's'}
          </button>
          {showVerified && <div className="vt-list" style={{ marginTop: 8 }}>{verified.map(row)}</div>}
        </>
      )}
    </>
  );
}
